import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './../../styles/admin/reportes_views.css';
import * as globals from './../../components/GlobalScripts.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

const Admin_reportes_propuestas = () => {
    const path_api = localStorage.getItem('path_api');
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState({});
    const [metrics, setMetrics] = useState(null);

    // Fechas de filtro: se calcula la fecha actual y hace un mes de diferencia
    const options = { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' };
    const formattedToday = new Date().toLocaleDateString('en-CA', options);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const formattedOneMonthAgo = oneMonthAgo.toLocaleDateString('en-CA', options);
    const [searchDateStart, setSearchDateStart] = useState(formattedOneMonthAgo);
    const [searchDateEnd, setSearchDateEnd] = useState(formattedToday);

    useEffect(() => {
        const user_cache = localStorage.getItem('user');
        if (user_cache) {
            let userParsed = JSON.parse(user_cache);
            if (userParsed) {
                setUser(userParsed);
            }
        }
        get_metrics(searchDateStart, searchDateEnd);
    }, [location]);

    const get_metrics = (dateStart, dateEnd) => {
        globals.openViewCharge();
        axios.post(`${path_api}/admin/reportes/propuestas.php`, {
            rute: globals.getRuteBackend(),
            session_id: globals.get_session_id(),
            keylogin: globals.get_keylogin(),
            searchDateStart: dateStart,
            searchDateEnd: dateEnd,
        })
            .then(response => {
                if (response.data.mensaje === 'success') {
                    setMetrics(response.data.metrics);
                } else {
                    globals.createAlert({
                        type: "error",
                        msg_title: "Error",
                        msg: response.data.mensaje || "No se obtuvieron las métricas",
                        effect: "opacidad 2s, desplazamiento 2s",
                        time_life: 4000
                    });
                }
            })
            .catch(error => {
                globals.createAlert({
                    type: "error",
                    msg_title: "Error",
                    msg: "Error al cargar las métricas",
                    effect: "opacidad 2s, desplazamiento 2s",
                    time_life: 4000
                });
            })
            .finally(() => {
                globals.closeViewCharge();
            });
    };

    const handleSearchStartChange = (e) => {
        const value = e.target.value;
        setSearchDateStart(value);
        const date = new Date(value);
        if (date.getFullYear() >= 2025) {
            get_metrics(value, searchDateEnd);
        }
    };

    const handleSearchEndChange = (e) => {
        const value = e.target.value;
        setSearchDateEnd(value);
        const date = new Date(value);
        if (date.getFullYear() >= 2025) {
            get_metrics(searchDateStart, value);
        }
    };

    // --- Gráfica de Propuestas por Estado (Pie) ---
    // Se calcula el porcentaje de cada estado basado en el total de propuestas
    let estadoLabels = [];
    let estadoPorcentajes = [];
    if (metrics && metrics.propuestas_por_estado && metrics.total_propuestas > 0) {
        estadoLabels = Object.keys(metrics.propuestas_por_estado).map(key => `${key} (${metrics.propuestas_por_estado[key]})`);
        estadoPorcentajes = Object.values(metrics.propuestas_por_estado).map(count => ((count / metrics.total_propuestas) * 100).toFixed(2));
    }
    const propuestasEstadoChartData = {
        labels: estadoLabels,
        datasets: [
            {
                data: estadoPorcentajes,
                backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56', '#2ecc71', '#4BC0C0'],
                hoverBackgroundColor: ['#36A2EB', '#FF6384', '#FFCE56', '#2ecc71', '#4BC0C0'],
            },
        ],
    };

    const propuestasEstadoChartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 1,
        plugins: {
            legend: {
                position: 'bottom',
            },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value + '%',
            },
        },
    };

    // --- Gráfica de Precio Promedio ---
    const precioChartData = {
        labels: ['Precio promedio'],
        datasets: [
            {
                label: 'Precio promedio',
                data: [metrics && metrics.precio_promedio ? Number(metrics.precio_promedio).toFixed(2) : 0],
                backgroundColor: ['#2ecc71'],
                hoverBackgroundColor: ['#2ecc71'],
            }
        ]
    };

    const precioChartOptions = {
        indexAxis: 'x',
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value,
            },
        },
        scales: {
            x: { beginAtZero: true }
        }
    };

    // --- Gráficas de métricas del Profesional (estado, ciudad, profesión) ---
    // Estilo horizontal similar al reporte de cuentas
    const horizontalChartContainerStyle = (count) => ({
        position: 'relative',
        height: count * 40 + 50 + 'px'
    });

    // Función para generar colores dinámicos en HSL
    const generateColors = (labels = []) => {
        return labels.map((_, index) => {
            const hue = Math.floor((360 / labels.length) * index);
            return `hsl(${hue}, 70%, 50%)`;
        });
    };

    // Profesional por Estado
    const profEstadoLabels = metrics && metrics.pro_profesional_estado ? Object.keys(metrics.pro_profesional_estado) : [];
    const profEstadoColors = generateColors(profEstadoLabels);
    const profEstadoChartData = {
        labels: profEstadoLabels,
        datasets: [
            {
                label: 'Profesionales por estado',
                data: metrics && metrics.pro_profesional_estado ? Object.values(metrics.pro_profesional_estado) : [],
                backgroundColor: profEstadoColors,
                hoverBackgroundColor: profEstadoColors,
            }
        ]
    };
    const profEstadoChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value,
            },
        },
    };

    // Profesional por Ciudad
    const profCiudadLabels = metrics && metrics.pro_profesional_ciudad ? Object.keys(metrics.pro_profesional_ciudad) : [];
    const profCiudadColors = generateColors(profCiudadLabels);
    const profCiudadChartData = {
        labels: profCiudadLabels,
        datasets: [
            {
                label: 'Profesionales por ciudad',
                data: metrics && metrics.pro_profesional_ciudad ? Object.values(metrics.pro_profesional_ciudad) : [],
                backgroundColor: profCiudadColors,
                hoverBackgroundColor: profCiudadColors,
            }
        ]
    };
    const profCiudadChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value,
            },
        },
    };

    // Profesional por Profesión
    const profProfesionLabels = metrics && metrics.pro_profesional_profesion ? Object.keys(metrics.pro_profesional_profesion) : [];
    const profProfesionColors = generateColors(profProfesionLabels);
    const profProfesionChartData = {
        labels: profProfesionLabels,
        datasets: [
            {
                label: 'Profesionales por profesión',
                data: metrics && metrics.pro_profesional_profesion ? Object.values(metrics.pro_profesional_profesion) : [],
                backgroundColor: profProfesionColors,
                hoverBackgroundColor: profProfesionColors,
            }
        ]
    };
    const profProfesionChartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 4,
                padding: 4,
                font: { weight: 'bold', size: 14 },
                formatter: (value) => value,
            },
        },
    };

    return (
        <div className="page-reportes-views container-section flex-column gap-20">
            <button onClick={() => navigate(-1)} className="flex-row gap-5 button-back">
                <svg width="20" viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24.163 41.125L38.7085 55.6658L38.7088 55.6662C39.2779 56.2353 39.7294 56.9109 40.0374 57.6545C40.3454 58.3981 40.5039 59.1951 40.5039 60C40.5039 60.8049 40.3454 61.6018 40.0374 62.3454C39.7294 63.089 39.2779 63.7647 38.7088 64.3338C38.1397 64.9029 37.464 65.3544 36.7204 65.6624C35.9768 65.9704 35.1798 66.1289 34.375 66.1289C33.5701 66.1289 32.7732 65.9704 32.0296 65.6624C31.2859 65.3544 30.6103 64.9029 30.0412 64.3338L5.04386 39.3365C5.04297 39.3356 5.04207 39.3347 5.04118 39.3338M24.163 41.125L36.5875 12.2125C36.878 11.9219 37.1085 11.577 37.2658 11.1974C37.423 10.8178 37.5039 10.4109 37.5039 10C37.5039 9.5891 37.423 9.18222 37.2658 8.8026C37.1085 8.42298 36.878 8.07805 36.5875 7.7875C36.2969 7.49695 35.952 7.26647 35.5724 7.10923C35.1928 6.95198 34.7859 6.87105 34.375 6.87105C33.9641 6.87105 33.5572 6.95198 33.1776 7.10923C32.798 7.26647 32.453 7.49695 32.1625 7.7875L7.1625 32.7875C6.87148 33.0778 6.64059 33.4226 6.48305 33.8023C6.32551 34.1819 6.24441 34.589 6.24441 35C6.24441 35.411 6.32551 35.818 6.48305 36.1977C6.64059 36.5774 6.87148 36.9222 7.1625 37.2125L5.04118 39.3338"
                        fill="var(--color-primary)" stroke="var(--color-primary)" strokeWidth="6" />
                </svg>
                <h4 style={{ color: "var(--color-primary)" }}>Volver</h4>
            </button>
            <h3>Reporte de propuestas</h3>

            <div className="container-search">
                <div className="search">
                    <label>A partir de la fecha:</label>
                    <input type="date" value={searchDateStart} onChange={handleSearchStartChange} className="input-primary" />
                </div>
                <div className="search">
                    <label>Hasta la fecha:</label>
                    <input type="date" value={searchDateEnd} onChange={handleSearchEndChange} className="input-primary" />
                </div>
            </div>

            {metrics && (
                <div className="chart-grid-container">
                    {/* Gráfica de Propuestas por Estado */}
                    <div className="chart-item">
                        <h4>Estatus de propuestas</h4>
                        <div style={{ position: 'relative', height: '300px' }}>
                            <Pie data={propuestasEstadoChartData} options={propuestasEstadoChartOptions} />
                        </div>
                    </div>

                    {/* Gráfica de Precio Promedio */}
                    <div className="chart-item">
                        <h4>Precio</h4>
                        <div style={{ position: 'relative', height: '300px' }}>
                            <Bar data={precioChartData} options={precioChartOptions} />
                        </div>
                    </div>

                    {/* Métricas del Profesional: Estado */}
                    {metrics.pro_profesional_estado && Object.keys(metrics.pro_profesional_estado).length > 0 && (
                        <div className="chart-item">
                            <h4>Propuestas por estado</h4>
                            <div style={horizontalChartContainerStyle(Object.keys(metrics.pro_profesional_estado).length)}>
                                <Bar data={profEstadoChartData} options={profEstadoChartOptions} />
                            </div>
                        </div>
                    )}

                    {/* Métricas del Profesional: Ciudad */}
                    {metrics.pro_profesional_ciudad && Object.keys(metrics.pro_profesional_ciudad).length > 0 && (
                        <div className="chart-item">
                            <h4>Propuestas por ciudad</h4>
                            <div style={horizontalChartContainerStyle(Object.keys(metrics.pro_profesional_ciudad).length)}>
                                <Bar data={profCiudadChartData} options={profCiudadChartOptions} />
                            </div>
                        </div>
                    )}

                    {/* Métricas del Profesional: Profesión */}
                    {metrics.pro_profesional_profesion && Object.keys(metrics.pro_profesional_profesion).length > 0 && (
                        <div className="chart-item">
                            <h4>Propuestas por profesión principal</h4>
                            <div style={horizontalChartContainerStyle(Object.keys(metrics.pro_profesional_profesion).length)}>
                                <Bar data={profProfesionChartData} options={profProfesionChartOptions} />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Admin_reportes_propuestas;
